import React, { useState } from "react";
import { Form, Input, Button as AntdButton, Card as AntdCard, Typography } from "antd";
import { Mail, Lock } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import logo from "../assets/images/XynexAI_Logo.png";
import { login } from "../store/slices/authSlice";

const { Title, Paragraph } = Typography;

const Login = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);

  const onFinish = async (values) => {
    try {
      const trimmedValues = {
        email: (values.email || "").trim(),
        password: (values.password || "").trim(),
      };
      // console.log("Login values:", trimmedValues);
      const result = await dispatch(
        login({
          email: trimmedValues.email,
          password: trimmedValues.password,
        })
      ).unwrap();
      // console.log("Login result:", result);
      toast.success("Successfully logged in!",{theme: "colored"});
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(
        err.message || "Login failed. Please check your credentials.",{theme: "colored"}
      );
    }
  };

  const handleCreateAccount = () => {
    navigate("/signup");
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Header */}
      <header className="flex justify-center items-center flex-shrink-0">
        <img
          src={logo}
          alt="XynexAi Logo"
          className="w-28 h-auto max-h-28 object-contain"
        />
      </header>

      {/* Main / Login card */}
      <main className="flex flex-1 items-center justify-center px-2 overflow-hidden">
        <AntdCard className="w-full max-w-md bg-white/5 border border-white/10 rounded-2xl">
          <div className="p-2 sm:p-2">
            <div className="text-center mb-4">
              <Title
                level={2}
                className="!m-0 !text-[clamp(1.5rem,2.5vw,2.25rem)] !font-semibold !text-white"
              >
                Welcome to XynexAI
              </Title>
              <Paragraph className="mt-2 sm:mt-3 !text-white text-sm sm:text-base">
                Please sign in to your account
              </Paragraph>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              className="space-y-2 sm:space-y-2"
              initialValues={{ email: "", password: "" }}
            >
              {/* Email */}
              <Form.Item
                label={<span className="text-gray-200 text-sm sm:text-base">Email</span>}
                name="email"
                rules={[{ required: true, message: "Please enter your email" }]}
              >
                <Input
                  prefix={<Mail className="w-5 h-5 text-gray-500" />}
                  type="email"
                  placeholder="Enter your email"
                  size="large"
                  className="h-12 bg-gray-50 border-gray-200 rounded-lg"
                  disabled={loading}
                />
              </Form.Item>

              {/* Password */}
              <Form.Item
                label={
                  <span className="text-gray-200 text-sm sm:text-base">Password</span>
                }
                name="password"
                rules={[
                  { required: true, message: "Please enter your password" },
                ]}
              >
                <Input.Password
                  prefix={<Lock className="w-5 h-5 text-gray-500" />}
                  placeholder="Enter your password"
                  size="large"
                  className="h-12 bg-gray-50 border-gray-200 rounded-lg"
                  disabled={loading}
                />
              </Form.Item>

              {error && (
                <div className="text-red-500 text-sm mb-4">{error}</div>
              )}

              {/* Forgot Password */}
              <div className="text-right">
                <button
                  type="button"
                  className="font-semibold text-[#cca1e4] hover:text-[#bd9bd1] transition-colors text-base"
                  onClick={() => navigate("/forgot-password")}
                  disabled={loading}
                >
                  Forgot Password?
                </button>
              </div>

              {/* Submit */}
              <Form.Item>
                <AntdButton
                  htmlType="submit"
                  type="primary"
                  size="large"
                  loading={loading}
                  className="w-full h-12 bg-[#9161ad] hover:!bg-[#BC9BCF]/90 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  {loading ? "Signing In..." : "SIGN IN"}
                </AntdButton>
              </Form.Item>
            </Form>

            {/* Divider */}
            <div className="my-6 flex items-center">
              <div className="flex-1 border-t border-gray-200" />
              <div className="px-4 text-gray-200">or</div>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            {/* Create Account */}
            <div className="flex items-center justify-center">
              <p className="text-white text-base">Don't have an account?</p>
              <AntdButton
                type="button"
                className="font-semibold text-[#cca1e4] hover:text-[#bd9bd1] transition-colors text-base"
                onClick={handleCreateAccount}
                disabled={loading}
              >
                Sign Up
              </AntdButton>
            </div>
          </div>
        </AntdCard>
      </main>

      {/* Footer */}
      <footer className="text-center text-white text-sm py-3 flex-shrink-0">
        © {new Date().getFullYear()} InteliquantAI Pvt Ltd. All rights reserved.
        <div className="flex justify-center gap-6 mt-2 text-[13px] text-white">
          <a
            onClick={() => navigate("/login/help")}
            className="hover:underline"
          >
            FAQ
          </a>
          <Link to="/terms-of-use" className="hover:text-white">Terms of Use</Link>
          <Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
        </div>
      </footer>
    </div>
  );
};
export default Login;
